import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Lightbulb, Copy, ExternalLink, CheckCircle2, Bookmark, ArrowRight, Loader2 } from "lucide-react";

export default function AllDataBridgeSetup() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/alldata-bridge/bookmarklet-code")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load bookmarklet"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
      </div>
    );
  }
  if (!data) return null;

  const copyHref = async () => {
    try {
      await navigator.clipboard.writeText(data.bookmarklet_href);
      toast.success("Bookmarklet code copied — paste into a new bookmark");
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-400 mb-2 flex items-center gap-1.5">
          <Lightbulb className="w-3 h-3" /> ALLDATA BRIDGE
        </div>
        <h1 className="font-display text-4xl font-semibold tracking-tight">Auto-Fetch Setup</h1>
        <p className="text-sm text-slate-400 mt-2 max-w-2xl leading-relaxed">
          Install the &quot;Send to TrueLine&quot; bookmarklet once. When you find an OEM procedure in ALLDATA,
          one click captures the URL and attaches it to the right Calibration Report card — no copy-paste.
        </p>
      </div>

      {/* Step 1 — drag the bookmarklet */}
      <Card className="bg-[#0B1120] border-slate-800 p-6 space-y-4" data-testid="bridge-step-1-card">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-blue-500/15 border border-blue-500/40 text-blue-300 flex items-center justify-center font-bold">1</div>
          <h2 className="font-display text-lg font-semibold">Show your Bookmarks Bar</h2>
        </div>
        <p className="text-sm text-slate-300 leading-relaxed">
          In Chrome / Edge / Brave: press <kbd className="px-1.5 py-0.5 bg-slate-800 border border-slate-700 rounded text-xs font-mono">Ctrl</kbd>{" "}
          <kbd className="px-1.5 py-0.5 bg-slate-800 border border-slate-700 rounded text-xs font-mono">Shift</kbd>{" "}
          <kbd className="px-1.5 py-0.5 bg-slate-800 border border-slate-700 rounded text-xs font-mono">B</kbd>{" "}
          (Mac: <kbd className="px-1.5 py-0.5 bg-slate-800 border border-slate-700 rounded text-xs font-mono">⌘</kbd>{" "}
          <kbd className="px-1.5 py-0.5 bg-slate-800 border border-slate-700 rounded text-xs font-mono">Shift</kbd>{" "}
          <kbd className="px-1.5 py-0.5 bg-slate-800 border border-slate-700 rounded text-xs font-mono">B</kbd>). Safari: View → Show Favorites Bar.
        </p>
      </Card>

      {/* Step 2 — drag the link */}
      <Card className="bg-[#0B1120] border-slate-800 p-6 space-y-4" data-testid="bridge-step-2-card">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-blue-500/15 border border-blue-500/40 text-blue-300 flex items-center justify-center font-bold">2</div>
          <h2 className="font-display text-lg font-semibold">Drag this button to your Bookmarks Bar</h2>
        </div>
        <div className="rounded-xl border-2 border-dashed border-amber-500/40 bg-amber-500/5 p-8 text-center">
          {/* The bookmarklet must be an anchor with javascript: href so it can be dragged. */}
          <a
            href={data.bookmarklet_href}
            onClick={(e) => { e.preventDefault(); toast.info("Don't click — drag this to your bookmarks bar."); }}
            draggable
            data-testid="bridge-bookmarklet-link"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 text-white font-semibold shadow-lg shadow-blue-500/20 cursor-grab active:cursor-grabbing transition"
          >
            <Bookmark className="w-4 h-4" />
            {data.display_name}
          </a>
          <div className="text-[11px] text-slate-500 mt-3 italic">
            ← drag this button up to your bookmarks bar
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span>Browser blocks drag-drop?</span>
          <Button
            variant="outline"
            size="sm"
            className="border-slate-700 text-xs h-7"
            onClick={copyHref}
            data-testid="bridge-copy-href-btn"
          >
            <Copy className="w-3 h-3 mr-1" /> Copy code
          </Button>
          <span className="text-slate-600">Right-click bookmarks bar → Add new page → paste as URL.</span>
        </div>
      </Card>

      {/* Step 3 — start a capture */}
      <Card className="bg-[#0B1120] border-slate-800 p-6 space-y-4" data-testid="bridge-step-3-card">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-blue-500/15 border border-blue-500/40 text-blue-300 flex items-center justify-center font-bold">3</div>
          <h2 className="font-display text-lg font-semibold">Start a capture from a Calibration Report</h2>
        </div>
        <ol className="space-y-2 text-sm text-slate-300 list-decimal list-inside">
          <li>Open any Calibration Report → find the operation card you need a procedure for.</li>
          <li>Click <Badge className="bg-amber-500/15 border border-amber-500/40 text-amber-300 mx-1">🎯 Auto-Capture</Badge> on that card.</li>
          <li>ALLDATA opens in a new tab + a small &quot;Waiting...&quot; modal in TrueLine.</li>
          <li>In ALLDATA: log in, navigate to the OEM procedure, then click <Badge className="bg-blue-500/15 border border-blue-500/40 text-blue-300 mx-1">Send to TrueLine</Badge> in your bookmarks bar.</li>
          <li>A picker opens with your active sessions → click <strong>Attach here</strong>. Done.</li>
        </ol>
      </Card>

      {/* Step 4 — test it */}
      <Card className="bg-emerald-500/5 border-emerald-500/30 p-6 space-y-3" data-testid="bridge-step-4-card">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-400" />
          <h2 className="font-display text-lg font-semibold text-emerald-300">Quick test</h2>
        </div>
        <p className="text-sm text-slate-300">
          Verify the bookmarklet works without opening ALLDATA — visit any webpage (like Google.com), then click <strong>Send to TrueLine</strong> in your bookmarks bar.
          The picker window should open and list your pending sessions.
        </p>
      </Card>

      {/* Reference */}
      <Card className="bg-[#0B1120] border-slate-800 p-4">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Bookmarklet target URL</div>
        <div className="font-mono text-xs text-slate-400 break-all">{data.pick_url}</div>
        <div className="text-[10px] text-slate-600 mt-2">
          This bookmarklet only sends the page URL and title — it does NOT scrape ALLDATA content, fully complying with their Terms of Service.
        </div>
      </Card>
    </div>
  );
}
